import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useOktaAuth } from '@okta/okta-react';

import {
  getCurrentUser,
  patchCurrentUserRole,
  postCurrentUser,
  postEndMasquerade,
  postStartMasquerade,
} from 'api';
import { settings } from 'config';
import { getErrorDetail } from 'helpers';
import { useSnackContext } from 'hooks/useSnackContext';
import { RoleEnum, UserProps } from 'types';
export const UserContext = createContext<{
  user: UserProps | null;
  userWithRoles: UserProps | null;
  loaded: boolean;
  startMasquerade: (org_id: number) => Promise<void>;
  endMasquerade: () => Promise<void>;
  updateCurrentUserActiveRole: (selectedRole: RoleEnum) => Promise<void>;
}>(undefined!);

export const UserProvider = (props: { children: ReactNode }) => {
  const { oktaAuth } = useOktaAuth();
  const [user, setUser] = useState<UserProps | null>(null);
  const [userWithRoles, setUserWithRoles] = useState<UserProps | null>(null);
  const [loaded, setLoaded] = useState(false);
  const { setSnack } = useSnackContext();

  const loadUser = useCallback(async () => {
    try {
      const { data } = await postCurrentUser();
      setUser(data);
      const { data: userRoles } = await getCurrentUser();
      setUserWithRoles(userRoles);
      setLoaded(true);
    } catch (e) {
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: getErrorDetail({
          error: e,
          fallback: 'Failed To Load User',
        }),
        error: e as Error,
      }));
      console.log('Error from usercontext', e);
      setUser(null);
      setUserWithRoles(null);
      setLoaded(false);
    }
  }, [setUser, setSnack, setUserWithRoles]);

  const updateCurrentUserActiveRole = useCallback(
    async (selectedRole: RoleEnum) => {
      try {
        await patchCurrentUserRole(selectedRole);
        await getCurrentUser();
      } catch (e) {
        setSnack((props) => ({
          ...props,
          severity: 'error',
          open: true,
          message: 'Failed To Get Current User',
          error: e as Error,
        }));
      }
    },
    [setSnack],
  );

  const startMasquerade = useCallback(
    async (org_id: number) => {
      try {
        await postStartMasquerade(org_id);
        await loadUser();
      } catch (e) {
        setSnack((props) => ({
          ...props,
          severity: 'error',
          open: true,
          message: 'Failed To Start Masquarade',
          error: e as Error,
        }));
      }
    },
    [setSnack, loadUser],
  );

  const endMasquerade = useCallback(async () => {
    try {
      await postEndMasquerade();
      await loadUser();
    } catch (e) {
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: 'Failed To End Masquarade',
        error: e as Error,
      }));
    }
  }, [setSnack, loadUser]);
  useEffect(() => {
    loadUser();
    return () => {
      setLoaded(false);
    };
  }, [loadUser]);
  useEffect(() => {
    const signOut = async () => {
      await oktaAuth.signOut({
        clearTokensBeforeRedirect: true,
        postLogoutRedirectUri: settings.APP_ORIGINAL_URI,
        state: 'logged_out',
      });
    };
    if (loaded && user && user.account_status === 'disabled') {
      signOut();
    }
  }, [user, loaded, oktaAuth]);

  const memoizedReturn = useMemo(
    () => ({
      user,
      loaded,
      startMasquerade,
      endMasquerade,
      userWithRoles,
      updateCurrentUserActiveRole,
    }),
    [
      user,
      loaded,
      startMasquerade,
      endMasquerade,
      userWithRoles,
      updateCurrentUserActiveRole,
    ],
  );
  return (
    <UserContext.Provider value={memoizedReturn}>
      {props.children}
    </UserContext.Provider>
  );
};
